import { Badge, Box, Button, Flex, HStack, List, ListItem, Text } from "@chakra-ui/react";
import { ClockCountdown } from "@phosphor-icons/react";
import { useState } from "react";
import { pendingChangeCount } from "utils/foundationPendingChanges";

/**
 * Says what approving this workstream would publish (ESG-4012).
 *
 * Edits land in the working set only; answers and AI Review keep reading the last approved figures until the
 * owner approves. Without this the page shows the new figures with nothing saying the rest of the platform
 * does not see them yet. Removed figures are listed by name, because they are no longer rows in the table and
 * this is the only place they appear.
 *
 * A compact single-line notice (white card, orange accent bar) rather than a filled orange box: it is a
 * status, not an error, and the per-row "pending approval" badges already carry the orange.
 */
export default function PendingChangesNotice({ pending }) {
  const [showRemoved, setShowRemoved] = useState(false);
  const added = pending?.added ?? [];
  const edited = pending?.edited ?? [];
  const removed = pending?.removed ?? [];
  const total = pendingChangeCount(pending);
  if (total === 0) return null;

  const counts = [
    { label: "added", n: added.length },
    { label: "edited", n: edited.length },
    { label: "removed", n: removed.length },
  ].filter((c) => c.n > 0);

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderLeft="3px solid"
      borderLeftColor="orange.400"
      borderRadius="md"
      px={4}
      py={3}
    >
      <Flex align="center" gap={3} wrap="wrap">
        <Box color="orange.500" flexShrink={0}>
          <ClockCountdown size={18} weight="fill" />
        </Box>
        <Text fontSize="14px" fontWeight="700" color="darkTeal">
          {total} change{total === 1 ? "" : "s"} pending approval
        </Text>
        <HStack spacing={1}>
          {counts.map((c) => (
            <Badge key={c.label} variant="subtle" colorScheme="gray" fontSize="11px">
              {c.n} {c.label}
            </Badge>
          ))}
        </HStack>
        <Text fontSize="13px" color="gray.500" flex="1" minW="200px">
          Data will not be used in disclosures until approved.
        </Text>
        {removed.length > 0 ? (
          <Button variant="link" size="xs" color="gray.600" onClick={() => setShowRemoved((v) => !v)}>
            {showRemoved ? "Hide removed" : `Show removed (${removed.length})`}
          </Button>
        ) : null}
      </Flex>
      {showRemoved && removed.length > 0 ? (
        <List fontSize="13px" color="gray.600" mt={2} pl={8} spacing={1}>
          {removed.map((change) => (
            <ListItem key={change.datapointUid}>
              {change.dataKey}
              {change.publishedValue ? (
                <Text as="span" color="gray.400">
                  {` — was ${change.publishedValue}${change.publishedUnit ? ` ${change.publishedUnit}` : ""}`}
                </Text>
              ) : null}
            </ListItem>
          ))}
        </List>
      ) : null}
    </Box>
  );
}
